import { useLazyQuery, useQuery } from "@apollo/client/react";
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import 'moment/locale/es';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { adaptDelegacionReclamosArray } from '../../../adapters/delegacionAdapter';
import { adaptTipoProfesionalArray } from '../../../adapters/tipoProfesionalAdapter';
import { ButtonOptionGroup } from '../../../components/buttonOptionGroup/ButtonOptionGroup';
import { CustomButton } from '../../../components/customButton/CustomButton';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import adaptHonorariosArray, { GET_HONORARIOS } from '../../../data/graphQL/queries/Honorarios/getHonorarios';
import { GET_DELEGACIONES_BY_USUARIO } from '../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario';
import { GET_ALL_TIPO_PROFESIONALES } from '../../../data/graphQL/queries/PlanCuidado/getTipoProfesionales';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import { handleClientTextFilter } from '../helpers/handleClientTextFilter';

const storageId = sessionStorage.getItem('idUsuario');

const ESTADO_AC = 'Activos';
const ESTADO_IN = 'Inactivos';
const ESTADO_ALL = 'Todos';
const estadosOptions = [ESTADO_AC, ESTADO_IN, ESTADO_ALL];

const TIPO_PROF_TODOS = { id: -1, displayName: 'Todos' };

const idUsuarioLoggeado = sessionStorage.getItem('idUsuario');

export const HonorariosContent = ({ allowedWrite, setChildrenAction }) => {
  const navigate = useNavigate();

  const [delegacionOrigen, setDelegacionOrigen] = useState(0);
  const [delegaciones, setDelegaciones] = useState([]);
  const [tipoProfesionales, setTipoProfesionales] = useState([]);

  const {
    honorarios,
    honorariosLoading,
    totalCount,
    filters,
    handleChangeFilters,
    handleTextFilter,
    datosOriginal,
    setHonorarios
  } = useGetHonorarios(delegacionOrigen);

  const { data: dataTipoProfesionales } = useQuery(GET_ALL_TIPO_PROFESIONALES);

  useEffect(() => {
    if (!dataTipoProfesionales) return;
    setTipoProfesionales(adaptTipoProfesionalArray(dataTipoProfesionales));
  }, [dataTipoProfesionales]);

  const initialColumns = [
    {
      headerName: '',
      sortable: false,
      field: 'action',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      renderCell: (cellProps) => {
        const { row } = cellProps;

        return (
          <PopoverModal>
            <div className="popover-modal-content">
              <div
                onClick={() => handleClickView(row)}
                className='item-option clickable-option'
                aria-hidden='true'
              >
                <VisibilityIcon className='edit-icon' />
                <span className='toggle-action'>Ver</span>
              </div>
              {allowedWrite && <div
                onClick={() => handleClickEdit(row)}
                className='item-option clickable-option'
                aria-hidden='true'
              >
                <EditIcon className='edit-icon' />
                <span className='toggle-action'>Editar</span>
              </div>}
            </div>
          </PopoverModal>);

      }
    },
    {
      width: 60,
      field: 'id',
      headerName: 'ID'
    },
    {
      width: 140,
      field: 'idDelegacion',
      headerName: 'Delegación',
      valueGetter: (idDelegacion) => {
        return delegaciones.find((delegacion) => delegacion.id === idDelegacion)?.displayName || '-';
      }
    },
    {
      width: 180,
      field: 'descripcion',
      headerName: 'Descripción'
    },
    {
      width: 200,
      field: 'idTipoProfesional',
      headerName: 'Tipo Profesional',
      valueGetter: (idTipoProfesional) => {
        return tipoProfesionales?.find((x) => x.id === idTipoProfesional)?.displayName;
      }
    },
    {
      width: 120,
      field: 'figuraProfesional',
      headerName: 'Figura Profesional'
    },
    {
      width: 150,
      field: 'clasificacionMonitoreo',
      headerName: 'Clasif. Monitoreo'
    },
    {
      width: 170,
      field: 'formaLiquidacion',
      headerName: 'Forma Liquidación'
    },
    {
      width: 170,
      field: 'abreviaciones',
      headerName: 'Programas'
    },
    {
      width: 150,
      field: 'valorPresencial',
      headerName: 'V. Presencial',
      valueGetter: (valorPresencial) => {
        return '$' + parseFloat(valorPresencial || 0).toLocaleString('es-AR');
      }
    },
    {
      width: 150,
      field: 'valorVirtual',
      headerName: 'V. Virtual',
      valueGetter: (valorVirtual) => {
        return '$' + parseFloat(valorVirtual || 0).toLocaleString('es-AR');
      }
    },
    {
      width: 90,
      field: 'activo',
      headerName: 'Activo',
      renderCell: ({ row }) => {
        const { activo } = row;
        const icon = activo ?
          <CheckIcon className='icon-grid' /> :
          <ClearIcon className='icon-grid' />;
        return <Tooltip title={activo ? 'Activo' : 'Inactivo'}>{icon}</Tooltip>;
      }
    },
    {
      width: 150,
      field: 'fechaExpira',
      headerName: 'F. Expira',
      type: 'date'
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns, true), [delegaciones, allowedWrite]);

  const [getDelegaciones, { data: dataDelegaciones, loading: delegacionesLoading }] = useLazyQuery(GET_DELEGACIONES_BY_USUARIO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataDelegaciones) return;
    setDelegaciones(adaptDelegacionReclamosArray(dataDelegaciones));
  }, [dataDelegaciones]);

  const [getDelegacionOrigen, { data: dataDelegacionOrigen }] = useLazyQuery(GET_USUARIO_DELEGACION_ORIGEN, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataDelegacionOrigen) return;
    if (dataDelegacionOrigen.usuarioFull.success) {
      const deleg = parseInt(dataDelegacionOrigen.usuarioFull.valorEsperado.delegacionOrigen, 10);
      sessionStorage.setItem('delegacionOrigen', deleg);
      setDelegacionOrigen(deleg);

      if (filters.idDelegacion == 0)
        handleChangeFilters('idDelegacion', deleg);
    }
  }, [dataDelegacionOrigen]);

  useEffect(() => {
    if (!allowedWrite) return;

    setChildrenAction(() =>
      <div className='top-buttons'>
        <CustomButton
          className='button-header'
          disabled={!delegaciones?.length || !tipoProfesionales?.length}
          label='Nuevo'
          onClick={handleClickCreate}
          variant='contained' />
      </div>
    );
  }, [setChildrenAction, delegacionOrigen, delegaciones, tipoProfesionales, allowedWrite]);

  useEffect(() => {
    if (!delegacionesLoading) {
      getDelegaciones({ variables: { idUsuario: JSON.parse(idUsuarioLoggeado, 10) } });
    }
    getDelegacionOrigen({ variables: { idUsuario: JSON.parse(storageId, 10) } });
  }, []);

  const handleClickCreate = () => {
    navigate(`/honorarios/nuevoHonorario`,
      {
        state: {
          idHonorario: 0,
          isNew: true,
          allowedWrite: true,
          delegaciones,
          tipoProfesionales
        }
      }
    );
  };

  const handleClickEdit = (row) => {
    navigate(`/honorarios/nuevoHonorario`,
      {
        state: {
          idHonorario: row.id,
          isNew: false,
          allowedWrite: true,
          delegaciones,
          tipoProfesionales
        }
      }
    );
  };

  const handleClickView = (row) => {
    navigate(`/honorarios/nuevoHonorario`,
      {
        state: {
          idHonorario: row.id,
          isNew: false,
          allowedWrite: false,
          delegaciones,
          tipoProfesionales
        }
      }
    );
  };

  const handleTextClean = () => {
    handleChangeFilters('txtFiltro', '');
    setHonorarios(datosOriginal);
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    handleChangeFilters(name, value);
  };

  return (
    <div className='grid-container requisitos-clientes-grilla'>
      <Grid
        container
        spacing={2}
        sx={{ marginBottom: '25px' }}>

        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <TextField
            label='Buscar'
            name='txtFiltro'
            size='small'
            type='text'
            fullWidth
            variant='outlined'
            value={filters?.txtFiltro}
            onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
            onKeyUp={handleTextFilter}
            sx={{ backgroundColor: 'white' }}
            InputProps={{
              endAdornment:
                <InputAdornment position='end'>
                  <SearchIcon
                    sx={{ cursor: 'pointer' }} />

                  <ClearIcon
                    onClick={handleTextClean}
                    sx={{ cursor: 'pointer' }} />

                </InputAdornment>

            }} />

        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Delegación</InputLabel>
            <Select
              labelId='select-delegacion'
              options={delegaciones}
              label='Delegación'
              onChange={handleChange}
              value={filters?.idDelegacion || undefined}
              name='idDelegacion'>

              {delegaciones && delegaciones.map((delegacion) => {
                return <MenuItem key={delegacion.id} value={delegacion.id}> {delegacion.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-area-responsable'>Tipo profesional</InputLabel>
            <Select
              labelId='select-area-responsable'
              options={tipoProfesionales}
              label='Tipo profesional'
              onChange={handleChange}
              value={filters?.tipoProfesional || undefined}
              name='tipoProfesional'>

              <MenuItem key={TIPO_PROF_TODOS.id} value={TIPO_PROF_TODOS.id}> {TIPO_PROF_TODOS.displayName} </MenuItem>

              {tipoProfesionales && tipoProfesionales.map((tipo) => {
                return <MenuItem key={tipo.id} value={tipo.id}> {tipo.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', flexWrap: 'wrap' }}>
            <ButtonOptionGroup
              key={filters?.estado}
              options={estadosOptions}
              onOptionClick={(v) => handleChange({ target: { name: 'estado', value: v } })}
              selectedOption={filters?.estado} />

          </Box>
        </Grid>
      </Grid>
      <DataGridGenerico
        columns={columns}
        totalCount={totalCount}
        rows={honorarios}
        toolbar={false}
        loading={honorariosLoading} />

    </div>);

};

const useGetHonorarios = (delegacionOrigen) => {
  const [honorarios, setHonorarios] = useState([]);
  const [datosOriginal, setDatosOriginal] = useState([]);

  const { filters, handleChangeFilters } = usePersistFilters({
    idDelegacion: delegacionOrigen,
    estado: 0,
    tipoProfesional: -1,
    txtFiltro: ''
  }, `HonorariosFilter`);

  const [getHonorarios, { data: dataHonorarios, error: errorHonorarios, honorariosLoading }] = useLazyQuery(GET_HONORARIOS, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataHonorarios) return;
    const adaptedData = adaptHonorariosArray(dataHonorarios);
    setHonorarios(handleClientTextFilter(filters?.txtFiltro, adaptedData));
    setDatosOriginal(adaptedData);
  }, [dataHonorarios]);

  useEffect(() => {
    if (!errorHonorarios) return;
    toast.error('No se pudo cargar la tabla de honorarios, intentelo nuevamente más tarde.');
  }, [errorHonorarios]);

  useEffect(() => {
    if (honorariosLoading) return;
    getHonorarios({
      variables: {
        where: {
          and: [
            filters.idDelegacion !== -1 ?
              { idDelegacion: { eq: filters.idDelegacion } } :
              {},
            filters.tipoProfesional !== -1 //Si la opción es -1 es todos, se borra el filtro
              ? { idTipoProfesional: { eq: filters.tipoProfesional } } :
              {},
            filters.estado !== 2 //Si la opción es 2 es todos, se borra el filtro
              ? { activo: { eq: !filters.estado } } :
              {}]

        }
      }
    });
  }, [filters?.estado, filters?.idDelegacion, filters?.tipoProfesional]);

  const handleTextFilter = () => {
    const inputBuscar = filters?.txtFiltro;

    setHonorarios(handleClientTextFilter(inputBuscar, datosOriginal));
  };

  return {
    honorarios: honorarios,
    honorariosLoading: false,
    totalCount: honorarios?.length || 0,
    filters,
    handleChangeFilters,
    handleTextFilter,
    datosOriginal,
    setHonorarios
  };
};